import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, ShieldCheck } from "lucide-react";
import { listTrustedDefinitions, type TrustedDefinition } from "@/lib/api";

/**
 * Trusted catalog page — every {@code TrustedDefinition} the
 * customer has designated. Renders the audit-trail
 * (approval history) inline so compliance reviewers can verify
 * who-approved-what without leaving the page.
 */
export function TrustedCatalogPage() {
  const tenantId = "_default";
  const [filter, setFilter] = useState("");

  const trusted = useQuery({
    queryKey: ["semantic-trusted", tenantId],
    queryFn: () => listTrustedDefinitions(tenantId),
  });

  const filtered =
    trusted.data?.filter((td) =>
      filter.trim()
        ? td.displayName.toLowerCase().includes(filter.trim().toLowerCase()) ||
          td.canonicalName.toLowerCase().includes(filter.trim().toLowerCase())
        : true,
    ) ?? [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-semantic-ink">
            Trusted catalog
          </h1>
          <p className="mt-0.5 text-sm text-semantic-slate">
            Every metric you've designated as canonical, with its full
            approval history and the IR shape that propagates back to
            source platforms.
          </p>
        </div>
        <input
          type="search"
          placeholder="Filter by name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-64 rounded-md border border-semantic-slate/30 bg-white px-3 py-1.5 text-sm text-semantic-ink placeholder:text-semantic-slate focus:border-semantic-primary focus:outline-none focus:ring-2 focus:ring-semantic-primary/20"
        />
      </header>

      {trusted.isLoading && <Skeleton />}
      {trusted.error && (
        <ErrorBanner message={(trusted.error as Error).message} />
      )}
      {trusted.data && trusted.data.length === 0 && <EmptyState />}
      {filtered.length > 0 && (
        <ul data-tour-id="trusted-list" className="space-y-4">
          {filtered.map((td) => (
            <li key={td.id}>
              <TrustedRow td={td} />
            </li>
          ))}
        </ul>
      )}
      {trusted.data && trusted.data.length > 0 && filtered.length === 0 && (
        <NoMatches />
      )}
    </div>
  );
}

function TrustedRow({ td }: { td: TrustedDefinition }) {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <div className="rounded-xl2 border border-semantic-slate/20 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck size={14} className="text-semantic-primary" aria-hidden />
            <span className="font-medium text-semantic-ink">{td.displayName}</span>
            <span className="rounded-full bg-semantic-mist px-2 py-0.5 font-mono text-2xs text-semantic-slate">
              {td.canonicalName}
            </span>
            <StatusPill status={td.status} />
          </div>
          {td.ir.description && (
            <p className="mt-1.5 max-w-3xl text-sm text-semantic-ink">
              {td.ir.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-2xs text-semantic-slate">
            <span>Owner <span className="font-mono text-semantic-ink">{td.owner}</span></span>
            <span>Version <span className="font-mono text-semantic-ink">{td.version}</span></span>
            <span>Type <span className="font-mono text-semantic-ink">{td.ir.inferredType}</span></span>
            {td.ir.dependencies.length > 0 && (
              <span>
                Depends on <span className="font-mono text-semantic-ink">
                  {td.ir.dependencies.join(", ")}
                </span>
              </span>
            )}
          </div>
          <pre className="mt-2 overflow-x-auto rounded bg-semantic-ink/5 px-2 py-1 font-mono text-2xs text-semantic-ink">
            {td.ir.dialectSidecar.verbatimSource}
          </pre>
        </div>
        <button
          type="button"
          onClick={() => setShowHistory((s) => !s)}
          className="inline-flex items-center gap-1 rounded-md border border-semantic-slate/30 bg-white px-2 py-1 text-2xs text-semantic-ink hover:bg-semantic-mist"
        >
          <ClipboardList size={11} aria-hidden />
          {td.approvalHistory.length} approval
          {td.approvalHistory.length === 1 ? "" : "s"}
        </button>
      </div>
      {showHistory && (
        <ol className="mt-3 space-y-2 border-l-2 border-semantic-primary/30 pl-3 text-2xs">
          {td.approvalHistory.map((a, i) => (
            <li key={i}>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-semantic-primary/10 px-1.5 py-0.5 font-medium text-semantic-primary">
                  {a.action}
                </span>
                <span className="font-mono text-semantic-ink">{a.approver}</span>
                <span className="text-semantic-slate">
                  · {new Date(a.approvedAt).toLocaleString()}
                </span>
                {a.previousVersion > 0 && (
                  <span className="text-semantic-slate">
                    · replaced v{a.previousVersion}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-semantic-ink">{a.reason}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: TrustedDefinition["status"] }) {
  const tone =
    status === "ACTIVE"
      ? "bg-semantic-success/15 text-semantic-success"
      : status === "EXPIRED"
        ? "bg-semantic-danger/15 text-semantic-danger"
        : "bg-semantic-slate/15 text-semantic-slate";
  return (
    <span className={`rounded-full px-2 py-0.5 text-2xs font-medium ${tone}`}>
      {status}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-xl2 bg-semantic-mist/60" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl2 border border-semantic-danger/30 bg-semantic-danger/5 p-4 text-sm text-semantic-danger">
      Couldn't load trusted catalog — {message}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl2 border border-dashed border-semantic-slate/30 bg-white p-10 text-center">
      <ShieldCheck size={28} aria-hidden className="mx-auto text-semantic-primary/60" />
      <p className="mt-2 text-sm font-medium text-semantic-ink">
        No trusted definitions yet.
      </p>
      <p className="mt-1 text-2xs text-semantic-slate">
        Designate one from the Conflicts page; the catalog is your
        compliance-grade source-of-truth for canonical metric definitions.
      </p>
    </div>
  );
}

function NoMatches() {
  return (
    <div className="rounded-xl2 border border-dashed border-semantic-slate/30 bg-white p-8 text-center text-sm text-semantic-slate">
      No trusted definitions match your filter.
    </div>
  );
}
